import type { FieldError } from 'react-hook-form';
import {
  DateRange,
  DateRangeProps,
  Range,
  RangeKeyDict,
} from 'react-date-range';
import React, {
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import cn from 'classnames';

import styles from './DateRangePicker.module.scss';

import ArrowIcon from '@assets/icons/down-icon.svg';
import ErrorIcon from '@assets/icons/error-icon.svg';
import CalendarIcon from '@assets/icons/selectbox-calendar-icon.svg';

import { useClickOutsideHandler } from '@utils/hooks';
import { useTranslation } from 'react-i18next';

export type DateValue = {
  startDate?: Date | string | undefined;
  endDate?: Date | string | undefined;
};

export interface DateInputProps extends DateRangeProps {
  label?: string;
  value?: DateValue;
  timeSelector?: boolean;
  error?: FieldError;
  setValue?: (event: DateValue) => void;
  buttonClassName?: string;
  labelTop?: boolean;
  styleType?: 'default' | 'targeting' | 'analytics';
  targetTime?: 'past' | 'future';
  elementSize?: 'sm' | 'md';
  onActive?: () => void;
  onClose?: () => void;
  isActive?: boolean;
  withPlaceholderLabel?: boolean;
  withLeftIcon?: boolean;
  disableFutureTime?: boolean;
  disablePastOptions?: string[];
}

const CustomDateRangePicker: React.FC<DateInputProps> = (
  props: DateInputProps,
) => {
  const {
    value,
    setValue,
    timeSelector = false,
    label,
    error,
    className,
    buttonClassName,
    labelTop = true,
    styleType = 'default',
    targetTime = 'past',
    elementSize = 'md',
    isActive = false,
    withPlaceholderLabel = false,
    withLeftIcon = false,
    disableFutureTime = false,
    onActive,
    onClose,
    disablePastOptions,
  } = props;

  const { t } = useTranslation('common');
  const [dateIsOpen, setDateIsOpen] = useState(false);
  const [isCustom, setIsCustom] = useState(false);

  const pastOptions = [
    { value: 'today', label: t('today') },
    { value: '7', label: t('lastDay', { day: 7 }) },
    { value: '30', label: t('lastDay', { day: 30 }) },
    { value: '90', label: t('lastDay', { day: 90 }) },
    { value: 'alltime', label: t('allTime') },
    { value: 'custom', label: t('custom') },
  ];

  const futureOptions = [
    { value: 'today', label: t('today') },
    { value: '7', label: t('nextDay', { day: 7 }) },
    { value: '30', label: t('nextDay', { day: 30 }) },
    { value: '90', label: t('nextDay', { day: 90 }) },
    { value: 'custom', label: t('custom') },
  ];

  const dataPickerRef = useClickOutsideHandler({
    onClose: (e) => {
      const optionsContainer = document.querySelector(`.${styles.options}`);
      if (optionsContainer?.contains(e.target as Node)) return;
      setDateIsOpen(false);
      setIsCustom(false);
    },
  });

  useEffect(() => {
    if (dateIsOpen) {
      onActive?.();
    } else {
      onClose?.();
    }
  }, [dateIsOpen]);

  useEffect(() => {
    if (!isActive && isCustom) {
      setIsCustom(false);
    }
    setDateIsOpen(isActive);
  }, [isActive]);

  const initialDateValues = useRef({
    startDate: value?.startDate,
    endDate: value?.endDate,
  });

  const [range, setRange] = useState<Range>({
    startDate: value?.startDate ? new Date(value?.startDate) : new Date(),
    endDate: value?.endDate ? new Date(value?.endDate) : new Date(),
    key: 'selection',
  });

  const getPastStartDate = (option: string | number) => {
    if (option === 'today') {
      return dayjs().hour(0).minute(0).second(0).toDate();
    }

    return dayjs()
      .subtract(Number(option), 'day')
      .startOf('day')
      .add(1, 'day')
      .toDate();
  };

  const selectPastRange = (option: string | number) => {
    if (option === 'alltime') {
      const stateValue = {
        startDate: undefined,
        endDate: dayjs().toDate(),
      };

      setRange({
        ...range,
        startDate: dayjs(0).toDate(),
        endDate: dayjs().toDate(),
      });

      if (!isCustom) {
        saveAndClose(stateValue);
      }
      return;
    }

    const startDate = getPastStartDate(option);

    const endDate = dayjs().toDate();

    const stateValue = {
      startDate,
      endDate,
    };

    setRange({
      ...range,
      ...stateValue,
    });

    if (!isCustom) {
      saveAndClose(stateValue);
    }
  };

  const getFutureEndDate = (option: string | number) => {
    if (option == 'today') {
      return dayjs().hour(23).minute(59).second(59).toDate();
    }
    return dayjs()
      .add(Number(option), 'day')
      .hour(23)
      .minute(59)
      .second(59)
      .toDate();
  };

  const getFutureStartDate = () => dayjs().hour(0).minute(0).second(0).toDate();

  const selectFutureRange = (option: string | number) => {
    const startDate = getFutureStartDate();

    const endDate = getFutureEndDate(option);

    const stateValue = {
      startDate,
      endDate,
    };

    setRange({
      ...range,
      ...stateValue,
    });

    if (!isCustom) {
      saveAndClose(stateValue);
    }
  };

  const updateTime = (type: 'startDate' | 'endDate', time: string) => {
    const hours = Number(time.split(':')[0]);
    const minutes = Number(time.split(':')[1]);

    if (type === 'startDate') {
      const stateValue = {
        endDate: range.endDate,
        startDate: dayjs(range.startDate).hour(hours).minute(minutes).toDate(),
      };

      setRange({
        ...range,
        ...stateValue,
      });
    } else {
      const stateValue = {
        startDate: range.startDate,
        endDate: dayjs(range.endDate).hour(hours).minute(minutes).toDate(),
      };

      setRange({
        ...range,
        ...stateValue,
      });
    }
  };

  const itemOnClick = (value: string) => {
    if (targetTime === 'past') selectPastRange(value);
    if (targetTime === 'future') selectFutureRange(value);
  };

  const saveAndClose = (value: DateValue) => {
    setValue && setValue(value);
    setDateIsOpen(false);
  };

  const difference = useMemo(() => {
    const { startDate, endDate } = range;
    const start = dayjs(startDate).startOf('day');
    const end = dayjs(endDate).add(1, 'day').startOf('day');
    return end.diff(start, 'day');
  }, [range]);

  const differenceDays = useMemo(
    () => (difference > 1 ? `${difference} days` : '1 day'),
    [difference],
  );

  const checkKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  const formatString = useMemo(() => {
    if (timeSelector) return 'DD/MM/YYYY HH:mm';
    return 'DD/MM/YYYY';
  }, [timeSelector]);

  const options = useMemo(() => {
    if (targetTime === 'future') return futureOptions;
    if (targetTime === 'past')
      return pastOptions?.filter((y) => !disablePastOptions?.includes(y.value));
  }, [targetTime, t]);

  const handleDateRangeChange = ({ selection }: RangeKeyDict) => {
    if (
      selection?.startDate &&
      selection?.endDate &&
      initialDateValues.current?.startDate &&
      initialDateValues.current?.endDate
    ) {
      const startDate = new Date(selection.startDate);
      const endDate = new Date(selection.endDate);

      const initialStartDate = new Date(initialDateValues.current.startDate);
      const initialEndDate = new Date(initialDateValues.current.endDate);

      startDate.setHours(initialStartDate.getHours());
      endDate.setHours(initialEndDate.getHours());
      startDate.setMinutes(initialStartDate.getMinutes());
      endDate.setMinutes(initialEndDate.getMinutes());

      selection = { startDate, endDate, key: 'selection' };
    }
    setRange(selection);
  };

  const valueToDisplayText = () => {
    const dateDiffDisplayText =
      value?.startDate &&
      value?.endDate &&
      `${dayjs(value?.startDate).format(formatString)} - ${dayjs(
        value?.endDate,
      ).format(formatString)}`;

    if (!withPlaceholderLabel) return dateDiffDisplayText;

    let labelValue = dateDiffDisplayText;

    let dayDiff = dayjs(value?.endDate).diff(value?.startDate, 'day', true);

    const isToday = dayjs().isSame(value?.endDate, 'day');

    if (isToday) {
      labelValue = 'Today';
    }

    if (value?.endDate && !value?.startDate) {
      labelValue = t('allTime');
    }
    const val = pastOptions
      ?.filter((y) => !disablePastOptions?.includes(y.value))
      .find((option) => option.value === Math.ceil(dayDiff).toString());

    if (val?.label && isToday) {
      labelValue = val.label;
    }

    return labelValue;
  };

  return (
    <div
      ref={dataPickerRef}
      className={cn('relative', className, {
        [styles.small]: elementSize === 'sm',
        [styles.targeting_style]: styleType === 'targeting',
        [styles.analytics_style]: styleType === 'analytics',
      })}
      onKeyDown={(e) => checkKeyDown(e)}
    >
      <div className={cn(styles.btn_wrapper)}>
        <button
          type="button"
          className={cn(dateIsOpen && styles.active, buttonClassName)}
          onClick={() => setDateIsOpen(!dateIsOpen)}
        >
          {withLeftIcon && (
            <div className="mr-1.5">
              <CalendarIcon />
            </div>
          )}

          {value && valueToDisplayText()}

          {label && (
            <span className="hidden opacity-0 pointer-events-none">
              {label}
            </span>
          )}
          <ArrowIcon
            className={cn(
              'ml-auto transition-transform',
              dateIsOpen && 'rotate-180',
            )}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            color="#D2DAE3"
          />
        </button>
        {label && (
          <span
            className={cn(
              styles.label,
              (value?.startDate || value?.endDate) && styles.label_top,
              dateIsOpen && styles.focus,
              (value?.startDate || value?.endDate) && !labelTop && '!hidden',
            )}
          >
            {label}
          </span>
        )}
      </div>
      <div
        className={cn(
          styles.custom_date_wrapper,
          dateIsOpen && styles.select_open,
        )}
      >
        <div className={cn(styles.date_select, isCustom && styles.custom_open)}>
          {isCustom && (
            <div className={styles.custom_date}>
              <DateRange
                className={cn('custom_date', timeSelector && 'time_select')}
                editableDateInputs={true}
                onChange={handleDateRangeChange}
                moveRangeOnFirstSelection={false}
                showMonthAndYearPickers={false}
                weekStartsOn={1}
                dateDisplayFormat="dd/MM/yyyy"
                ranges={[range]}
                minDate={targetTime === 'future' ? dayjs().toDate() : undefined}
                maxDate={disableFutureTime ? dayjs().toDate() : undefined}
              />
              {timeSelector && (
                <>
                  <input
                    className={cn(styles.time_input, styles.start_time)}
                    type="time"
                    defaultValue={dayjs(value?.startDate).format('HH:mm')}
                    onChange={(e) => {
                      const date = e.target.value;
                      updateTime('startDate', date);
                    }}
                  />
                  <input
                    className={cn(styles.time_input, styles.end_time)}
                    type="time"
                    defaultValue={dayjs(value?.endDate).format('HH:mm')}
                    onChange={(e) => {
                      const date = e.target.value;
                      updateTime('endDate', date);
                    }}
                  />
                </>
              )}
            </div>
          )}
          <div className={styles.options}>
            <ul>
              {options &&
                !!options.length &&
                options?.map((item) => {
                  if (item.value === 'custom') {
                    return (
                      <li
                        className={cn(
                          styles.custom_btn,
                          isCustom && styles.active,
                        )}
                        key={item.value}
                        onClick={() => setIsCustom(!isCustom)}
                      >
                        {item.label}
                      </li>
                    );
                  }
                  return (
                    <li
                      className={cn(
                        (targetTime === 'past'
                          ? range.endDate?.getDate()
                          : range.startDate?.getDate()) ===
                          getFutureStartDate().getDate() &&
                          Number(difference) ===
                            (item.value === 'today' ? 1 : Number(item.value)) &&
                          styles.active,
                      )}
                      onClick={() => itemOnClick(item.value)}
                      key={item.value}
                    >
                      {item.label}
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
        {isCustom && (
          <div className="flex items-center justify-between p-5 border-t border-gray-400">
            <button
              type="button"
              onClick={() => {
                setIsCustom(false);
                setDateIsOpen(false);
              }}
              className="px-6 py-[10px] rounded bg-gray-100 text-black font-inter !text-sm"
            >
              Cancel
            </button>
            <div className="flex items-center">
              <p className="font-inter text-sm leading-lg text-gray-700 mr-5">
                Selected: {differenceDays}
              </p>
              <button
                type="button"
                onClick={() => {
                  saveAndClose({
                    startDate: range?.startDate,
                    endDate: range?.endDate,
                  });
                }}
                className="px-6 py-[10px] rounded bg-bluesmart text-white font-inter !text-sm"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
      {!!error && (
        <div className={styles.error_div}>
          <ErrorIcon
            width="18"
            height="18"
            viewBox="0 0 32 32"
            color="#EA0F0F"
          />
          <p>{error?.message || 'Invalid'}</p>
        </div>
      )}
    </div>
  );
};

export default CustomDateRangePicker;
